import _ from 'lodash';
import React, { useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';
import { useTranslation, Trans } from 'react-i18next';
import cn from 'classnames';
import Alert from 'shared/components/Alert';
import Collapsable from 'shared/components/Collapsable';
import FileIcon from 'shared/components/FileIcon';
import Spinner from 'shared/components/Spinner';
import Tip from 'shared/components/Tip';
import TranslateError from 'shared/components/TranslateError';
import { propTypes as userPropTypes } from 'shared/proptypes/User';
import { propTypes as attachmentPropTypes } from 'shared/proptypes/Attachment';
import useForm from 'shared/hooks/useForm';
import useFileUpload from 'shared/hooks/useFileUpload';
import { isEmailValid, isStringValid, isPhoneValid } from 'shared/lib/validators';
import { Input, ButtonsRow, Checkbox, Select, Label } from 'shared/components/Form';
import FileUpload from 'shared/components/FileUpload';
import Divider from 'shared/components/Divider';
import {
  ATTACHMENT_TYPE,
  ATTACHMENT_MAX_SIZE,
  ATTACHMENT_MAX_SIZE_MB,
  I18N_LANGUAGES,
  DEFAULT_LANGUAGE_CODE,
  JOB_APPLICATION_EXPORT_COMPANIES,
  JOB_APPLICATION_EXPORT_AGREEMENT,
  LINKEDIN_URL_MASK,
  CURRENCIES,
  RATE_PERIOD,
  CURRENCY
} from 'shared/constants';
import { userportal as routes } from 'shared/routes';
import apiClient from 'userportal/lib/ApiClient';
import { selectors as authSelectors } from 'userportal/ducks/auth';
import { selectors as profileSelectors } from 'userportal/ducks/profile';
import styles from './MessageForm.module.scss';

export const JobApplicationMessageForm = ({
  jobTitle,
  companyId,
  companyName,
  resumeRequired,
  type,
  user,
  attachments,
  processing,
  onSubmit,
  onCancel,
  error,
  className,
  scrollToError,
  isFromOffer,
  requirePhone,
  preferredLanguage,
}) => {
  const fileRef = useRef();
  const { t, i18n } = useTranslation('common');
  const [files, setFiles] = useState({});
  const [fileError, setFileError] = useState(null);
  const loggedIn = useSelector(authSelectors.isLoggedIn);
  const isAnyParseInProgress = useSelector(profileSelectors.isAnyParseInProgress);
  const { uploadFile, progress, abortUpload } = useFileUpload({ apiClient });

  const requireContact = type === 'apply' && !loggedIn;
  const agreementNeeded = JOB_APPLICATION_EXPORT_COMPANIES.includes(companyId);

  const currencies = useMemo(() => CURRENCIES.map(code => ({
    value: code,
    label: t(`common:currency.${code}`)
  })), [i18n.language]);

  const ratePeriods = useMemo(() => _.values(RATE_PERIOD)
    .filter(value => value !== RATE_PERIOD.PIECE)
    .map(value => ({
      value,
      label: t(`common:ratePeriod.${value}`)
    })), [i18n.language]);

  const handleAddMissingResume = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (fileRef.current && fileRef.current.click) {
      fileRef.current.click();
    }
  };

  const validate = (data) => {
    const errs = {};
    if (!isStringValid(data.message) && (data.addNote === false || type === 'apply')) {
      errs.message = t('validation.fillMessage');
    }

    if (requireContact && !isStringValid(data.firstname)) {
      errs.firstname = t('validation.fillFirstname');
    }

    if (requireContact && !isStringValid(data.lastname)) {
      errs.lastname = t('validation.fillLastname');
    }

    if (requireContact && !isEmailValid(data.email)) {
      errs.email = t('common:validation.fillValidEmail');
    }

    if (data.linkedIn && !(isStringValid(data.linkedIn) && LINKEDIN_URL_MASK.test(data.linkedIn))) {
      errs.linkedIn = t('validation.invalidLinkedInUrl');
    }

    if (requirePhone && !isPhoneValid((data.phone || '').replace(/[^+0-9]+/g, '').trim())) {
      errs.phone = t('validation.fillPhone');
    }

    if (!isStringValid(data.note) && data.addNote === true) {
      errs.note = t('validation.fillNote');
    }

    if (data.agreement === false) {
      errs.agreement = t('validation.notCheckedAgreement');
    }

    if (data.termsAgreement === false) {
      errs.termsAgreement = t('validation.notCheckedAgreement');
    }

    return errs;
  };

  const userName = user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : null;
  let defaultMessage = '';
  let label = t('applicationForm.messageForEmployerLink');
  if (type === 'apply') {
    label = null;
    if (isFromOffer) {
      defaultMessage = jobTitle
        ? t('applicationForm.defaultMessagePositionFromOffer', { context: user.gender, name: jobTitle })
        : t('applicationForm.defaultMessageThisPositionFromOffer', { context: user.gender });
    } else {
      defaultMessage = jobTitle
        ? t('applicationForm.defaultMessagePosition', { context: user.gender, name: jobTitle })
        : t('applicationForm.defaultMessageThisPosition', { context: user.gender });
    }
  }

  if (defaultMessage.length > 0 && userName) {
    defaultMessage += t('applicationForm.messageFooter', { userName });
  }

  if (preferredLanguage) {
    label = <>{label}<small>({t('preferredLanguageLc')}: {t(`languageByCode.${preferredLanguage}`)})</small></>;
  }

  const resumes = type === 'apply' ? attachments.filter(attachment => attachment.type === ATTACHMENT_TYPE.CV) : [];
  const defaultAttachments = resumes.reduce((acc, curr) => {
    acc[curr.id] = true;
    return acc;
  }, {});

  const filesEntries = Object.entries(files);

  const onFormSubmit = (data) => {
    onSubmit({
      ...data,
      files: filesEntries.map(([, { filename, originalName }]) => ({ filename, originalName }))
    });
  };

  const {
    values, errors, hasError, handleSubmit, handleChange, setValue
  } = useForm(onFormSubmit, {
    type,
    addNote: false,
    attachments: defaultAttachments,
    note: null,
    firstname: '',
    lastname: '',
    phone: '',
    email: '',
    linkedIn: '',
    createProfile: true,
    message: defaultMessage,
    agreement: agreementNeeded ? false : undefined,
    termsAgreement: type === 'apply' ? false : undefined,
    expectedSalaryCurrency: CURRENCY.CZK,
    expectedSalaryPeriod: RATE_PERIOD.MONTHLY,
    availability: '',
  }, validate, { resetOnSubmit: false, scrollToError, scrollToTop: false });

  const onAttachmentChange = ({ target: { name, checked } }) => {
    const { [name]: prev, ...rest } = values.attachments;
    if (checked) {
      rest[name] = true;
    }
    setValue('attachments', rest);
  };

  const handleFileSelect = (event) => {
    event.persist();
    event.preventDefault();

    setTimeout(async () => {
      const file = event.dataTransfer
        ? event.dataTransfer.files[0]
        : event.target.files[0];

      if (!file) {
        setFileError(t('error.cannotUploadFile'));
        return;
      }

      if (file.size > ATTACHMENT_MAX_SIZE) {
        setFileError(t('userProfile.attachments.fileTooLarge', { mb: ATTACHMENT_MAX_SIZE_MB }));
        return;
      }

      setFileError(null);
      const id = v4();
      setFiles(prev => ({
        ...prev,
        [id]: {
          file
        }
      }));

      const uploaded = await uploadFile(file);
      if (uploaded) {
        setFiles(prev => ({
          ...prev,
          [id]: {
            file,
            ...uploaded
          }
        }));
      }
    });
  };

  const handleFileDelete = id => () => setFiles((prev) => {
    const { [id]: _removed, ...rest } = prev;
    return rest;
  });

  const handleAbortUpload = id => () => {
    abortUpload();
    handleFileDelete(id)();
  };

  const placeholder = t('applicationForm.messageForEmployer');

  const isResumeSelected = !!resumes.find(resume => values.attachments[resume.id] === true);

  const missingResume = type === 'apply'
    ? (resumeRequired && !isResumeSelected && filesEntries.length === 0)
    : false;

  const externalAgreementLinkUrl = JOB_APPLICATION_EXPORT_AGREEMENT[companyId] && JOB_APPLICATION_EXPORT_AGREEMENT[companyId].LINK_URL
    ? JOB_APPLICATION_EXPORT_AGREEMENT[companyId].LINK_URL[i18n.language] || JOB_APPLICATION_EXPORT_AGREEMENT[companyId].LINK_URL[DEFAULT_LANGUAGE_CODE]
    : null;

  const isUploadInProgress = (progress > 0 && progress < 100);

  return (
    <>
      {isAnyParseInProgress && (
        <Spinner splash top>
          {t('userportal:page.user.profile.parseInProgress')}
        </Spinner>
      )}
      <form onSubmit={handleSubmit} className={cn(styles.form, className)}>
        {error && (<Alert variant="error"><TranslateError>{error}</TranslateError></Alert>)}
        <Input
          autoFocus
          className={cn(styles.message, loggedIn ? 'mb16' : null)}
          error={errors.message}
          label={label}
          placeholder={placeholder}
          iconClass="la la-sticky-note"
          rows={6}
          multiline
          name="message"
          value={values.message}
          onChange={handleChange}
        />
        {(requirePhone || requireContact) && (
          <div className="row">
            {requireContact && (
              <>
                <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                  <Input
                    error={errors.firstname}
                    label={t('common:form.firstname')}
                    iconClass="la la-user"
                    name="firstname"
                    value={values.firstname}
                    type="text"
                    onChange={handleChange}
                  />
                </div>
                <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                  <Input
                    error={errors.lastname}
                    label={t('common:form.lastname')}
                    iconClass="la la-user"
                    name="lastname"
                    value={values.lastname}
                    type="text"
                    onChange={handleChange}
                  />
                </div>
                <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                  <Input
                    error={errors.email}
                    label={t('common:form.email')}
                    iconClass="la la-at"
                    name="email"
                    value={values.email}
                    type="text"
                    onChange={handleChange}
                  />
                </div>
                <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                  <Input
                    error={errors.linkedIn}
                    label={t('common:form.linkedInUrl')}
                    iconClass="la la-linkedin"
                    name="linkedIn"
                    value={values.linkedIn}
                    type="text"
                    onChange={handleChange}
                  />
                </div>
              </>
            )}
            {requirePhone && (
              <div className={requireContact ? 'col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12' : 'col-xl-4 col-lg-6 col-md-6 col-sm-8 col-xs-12'}>
                <Input
                  error={errors.phone}
                  label={t('common:form.phone')}
                  labelHint={!requireContact ? t('applicationForm.phoneNote') : null}
                  labelHintIconClassName="la la-info-circle"
                  iconClass="la la-phone-alt"
                  name="phone"
                  value={values.phone}
                  type="text"
                  onChange={handleChange}
                />
              </div>
            )}
            {requireContact && (
              <div className="col-12 mb16">
                <Checkbox
                  fullWidth
                  name="createProfile"
                  value={values.createProfile}
                  onChange={handleChange}
                  label={t('applicationForm.createProfile')}
                  labelHint={t('applicationForm.createProfileHint')}
                />
              </div>
            )}
          </div>
        )}

        <div className="mb8">{t('applicationForm.addAttachments')}</div>
        {attachments.length > 0 && (
          <div className={styles.attachments}>
            <Collapsable maxHeight={50} enabled={attachments.length > 2}>
              {attachments.map(attachment => (
                <Checkbox
                  fullWidth
                  key={attachment.id}
                  name={attachment.id}
                  value={values.attachments[attachment.id]}
                  onChange={onAttachmentChange}
                  label={(
                    <>
                      <FileIcon mime={attachment.mime} />
                      {`${attachment.title} (${t(`attachmentType.${attachment.type}`)})`}
                    </>
                  )}
                />
              ))}
            </Collapsable>
          </div>
        )}

        {missingResume && (
          <Tip small={false} type="warning" className={styles.resumeRequired}>
            <strong>{t('applicationForm.resumeIsRequired')}</strong>
            <Trans
              i18nKey={resumes.length > 0 ? 'applicationForm.selectRequiredResume' : 'applicationForm.uploadRequiredResume'}
            >
              <Link to="" onClick={handleAddMissingResume} />
            </Trans>
          </Tip>
        )}

        {filesEntries.map(([id, file]) => (
          <FileUpload
            key={id}
            buttonTitle={t('applicationForm.addDocument')}
            file={file.file}
            uploadProgress={file.filename ? 100 : progress}
            removeFile={handleFileDelete(id)}
            onAbortUpload={handleAbortUpload(id)}
            onFileSelect={handleFileSelect}
          />
        ))}
        {filesEntries.length < 3 && (
          <div className={filesEntries.length > 0 ? 'pt16' : ''}>
            {!isUploadInProgress && (
              <FileUpload
                ref={fileRef}
                disabled={processing}
                fileError={fileError}
                buttonTitle={t('applicationForm.addDocument')}
                onFileSelect={handleFileSelect}
              />
            )}
          </div>
        )}

        {type === 'apply' && (
          <>
            <Divider spacing="xLarge" labelClassName={styles.dividerLabel}>
              {t('applicationForm.detailsSectionTitle')}
            </Divider>
            <div className={styles.details}>
              <div className={styles.salary}>
                <div>
                  <Label htmlFor="expectedSalary">{t('applicationForm.expectedSalary')} <small>({t('optional')})</small></Label>
                </div>
                <Input
                  inline
                  placeholder={t('common:form.amountMoney')}
                  iconClass="la la-money"
                  if="expectedSalary"
                  name="expectedSalary"
                  value={values.expectedSalary}
                  type="number"
                  onChange={handleChange}
                />
                <Select
                  name="expectedSalaryCurrency"
                  options={currencies}
                  value={values.expectedSalaryCurrency}
                  onChange={handleChange}
                />
                <Select
                  name="expectedSalaryPeriod"
                  options={ratePeriods}
                  value={values.expectedSalaryPeriod}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.availability}>
                <Input
                  label={<>{t('applicationForm.availability', { context: user.gender })} <small>({t('optional')})</small></>}
                  placeholder={t('applicationForm.availabilityPlaceholder')}
                  iconClass="la la-sticky-note"
                  name="availability"
                  value={values.availability}
                  type="text"
                  onChange={handleChange}
                />
              </div>
            </div>
          </>
        )}

        {JOB_APPLICATION_EXPORT_AGREEMENT[companyId] && (
          <>
            <Divider spacing="medium" />
            <Checkbox
              fullWidth
              name="agreement"
              value={values.agreement}
              error={errors.agreement}
              onChange={handleChange}
              label={t(JOB_APPLICATION_EXPORT_AGREEMENT[companyId].LABEL_ID)}
            />
            {JOB_APPLICATION_EXPORT_AGREEMENT[companyId].LINK_ID && externalAgreementLinkUrl && (
              <p className={styles.agreementLink}>
                <Trans
                  i18nKey={JOB_APPLICATION_EXPORT_AGREEMENT[companyId].LINK_ID}
                  values={{ linkUrl: externalAgreementLinkUrl }}
                >
                  <a href={externalAgreementLinkUrl} target="_blank" rel="noopener noreferrer" />
                </Trans>
              </p>
            )}
          </>
        )}

        {type === 'apply' && (
          <div className={JOB_APPLICATION_EXPORT_AGREEMENT[companyId] ? 'mt16' : undefined}>
            {!JOB_APPLICATION_EXPORT_AGREEMENT[companyId] && <Divider spacing="medium" />}
            <Checkbox
              fullWidth
              name="termsAgreement"
              value={values.termsAgreement}
              error={errors.termsAgreement}
              onChange={handleChange}
              label={(
                <>
                  {t('userportal:page.job.applicationTermsAgreement')}
                  {' '}
                  <Trans i18nKey="userportal:page.signup.agreementRecipient" values={{ company: companyName }}>
                    <strong />
                  </Trans>
                </>
              )}
            />
            <p className={styles.agreementLink}>
              {t('userportal:page.signup.agreementLinks')}
              {' '}
              <a href={routes.tos} target="_blank" rel="noopener noreferrer">{t('userportal:footer.tos')}</a>
              {', '}
              <a href={routes.gdpr} target="_blank" rel="noopener noreferrer">{t('userportal:footer.gdpr')}</a>.
            </p>
          </div>
        )}

        <ButtonsRow
          onSubmit={handleSubmit}
          onCancel={onCancel}
          processing={processing}
          submitDisabled={hasError || missingResume || isUploadInProgress}
          cancelDisabled={isUploadInProgress}
          submitTitle={t('button.send')}
          cancelTitle={t('button.close')}
        />
      </form>
    </>
  );
};

JobApplicationMessageForm.propTypes = {
  jobTitle: PropTypes.string,
  companyId: PropTypes.string,
  companyName: PropTypes.string,
  resumeRequired: PropTypes.bool,
  type: PropTypes.oneOf(['apply', 'message']),
  user: PropTypes.shape(userPropTypes),
  attachments: PropTypes.arrayOf(PropTypes.shape(attachmentPropTypes)),
  processing: PropTypes.bool,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  error: PropTypes.string,
  className: PropTypes.string,
  scrollToError: PropTypes.bool,
  isFromOffer: PropTypes.bool,
  requirePhone: PropTypes.bool,
  preferredLanguage: PropTypes.oneOf(I18N_LANGUAGES),
};

JobApplicationMessageForm.defaultProps = {
  jobTitle: null,
  companyId: null,
  companyName: null,
  resumeRequired: false,
  type: null,
  user: {},
  attachments: [],
  processing: false,
  onSubmit() {},
  onCancel() {},
  error: null,
  className: null,
  scrollToError: true,
  isFromOffer: false,
  requirePhone: false,
  preferredLanguage: null,
};

export default JobApplicationMessageForm;
